/**
 * Persisted export-column selection.
 *
 * Column choice deliberately does NOT live in the URL alongside the filters: it
 * does not change which rows are queried, only what a download contains, and 30+
 * keys would swamp an otherwise shareable link. But it does need to survive a
 * reload — re-picking columns on every visit is exactly the kind of friction that
 * makes an export feature go unused.
 *
 * localStorage, keyed per resource, following the pattern `LeadsPage` uses for its
 * view mode (lazy `useState` initializer + an effect to write back).
 */

import { useEffect, useState } from 'react'
import { defaultColumnsFor, resolveColumns, type ReportResource } from '@limo/shared'

const storageKey = (resource: ReportResource): string => `report-columns-${resource}`

/**
 * Read a stored selection, dropping any keys that no longer exist.
 *
 * Validation goes through `resolveColumns`, which is already the single source of
 * truth for what a column is (and is unit-tested for dropping unknown keys). So a
 * selection saved before a column was renamed degrades to the columns that still
 * exist rather than producing empty cells.
 */
function readStored(resource: ReportResource): readonly string[] | null {
  try {
    const raw = localStorage.getItem(storageKey(resource))
    if (!raw) return null

    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null

    const keys = parsed.filter((k): k is string => typeof k === 'string')
    const valid = resolveColumns(resource, keys).map((c) => c.key)

    // An all-invalid selection is treated as absent, so the caller falls back to
    // defaults instead of rendering a headerless export.
    return valid.length > 0 ? valid : null
  } catch {
    // Corrupt JSON, or localStorage unavailable (private mode / disabled).
    // Falling back to defaults is always safe here.
    return null
  }
}

export function useExportColumns(
  resource: ReportResource
): [readonly string[], (next: readonly string[]) => void] {
  const [selected, setSelected] = useState<readonly string[]>(
    () => readStored(resource) ?? [...defaultColumnsFor(resource)]
  )

  useEffect(() => {
    try {
      localStorage.setItem(storageKey(resource), JSON.stringify(selected))
    } catch {
      // Persistence is a convenience — never break the page over it.
    }
  }, [resource, selected])

  return [selected, setSelected]
}
